import React, { useEffect, useState } from "react";
import { api } from "../../app/Api/ApiSlice";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import Loader from "../../components/Loader";

const OrgSettings = () => {
    const { name } = useParams();

    // Loading states
    const [isLoading, setIsLoading] = useState(false);
    const [savingLogo, setSavingLogo] = useState(false);
    const [savingBanner, setSavingBanner] = useState(false);
    const [savingDetails, setSavingDetails] = useState(false);
    const [savingAddress, setSavingAddress] = useState(false);
    const [savingContacts, setSavingContacts] = useState(false);

    // Profile
    const [storeName, setStoreName] = useState("");
    const [tagline, setTagline] = useState("");
    const [description, setDescription] = useState("");
    const [categories, setCategories] = useState([]); // FIXED: Added missing state

    // Address
    const [country, setCountry] = useState("");
    const [state, setState] = useState("");
    const [city, setCity] = useState("");
    const [street, setStreet] = useState("");
    const [building, setBuilding] = useState("");
    const [room, setRoom] = useState("");
    const [postalCode, setPostalCode] = useState("");

    // Contacts
    const [email, setEmail] = useState("");
    const [website, setWebsite] = useState("");
    const [phone, setPhone] = useState("");
    const [phone2, setPhone2] = useState("");

    // Media
    const [logo, setLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState("");
    const [banner, setBanner] = useState(null);
    const [bannerPreview, setBannerPreview] = useState("");

    // deleting
    const [deleting, setDeleting] = useState(false);
    const [typed, setTyped] = useState("");

    // ---------------- CATEGORIES ----------------
    const categoryOptions = [
        "homecare", "groceries", "furnitures", "beauty", "fashions", "stationery",
        "food&restaurants", "phone&accessories", "computer&accessories", "cars&accessories",
        "vacations&holidays", "videos&photography", "gifts", "construction&building",
        "sports&fitness", "farmproduce", "agriculture&accessories", "financialServices",
        "healthServices", "education&equipments", "entertainment", "eventFacilities",
        "hobbies", "manufacturingMachineries", "it&software", "securityServices", "others"
    ];

    const addCategory = (cat) => {
        if (categories.includes(cat)) return toast.error("Category already added");
        if (categories.length >= 3) return toast.error("Maximum 3 categories allowed");
        setCategories(prev => [...prev, cat]);
    };

    const removeCategory = (cat) => setCategories(prev => prev.filter(c => c !== cat));

    // Fetch org data
    useEffect(() => {
        const getStore = async () => {
            try {
                setIsLoading(true);
                const { data } = await api.get(`/stores/${name}`);
                const store = data.store;

                setLogoPreview(store.logo?.url)
                setBannerPreview(store.banner?.url)

                setStoreName(store?.name || "");
                setTagline(store?.tagline || "");
                setDescription(store?.description || "");
                // FIXED: Handle categories as an array
                setCategories(Array.isArray(store?.categories) ? store.categories : []);

                setCountry(store?.address?.country || "");
                setState(store?.address?.state || "");
                setCity(store?.address?.city || "");
                setStreet(store?.address?.street || "");
                setBuilding(store?.address?.building || "");
                setRoom(store?.address?.room || "");
                setPostalCode(store?.address?.postalCode || "");

                setEmail(store?.contacts?.email || "");
                setWebsite(store?.contacts?.website || "");
                setPhone(store?.contacts?.phone || "");
                setPhone2(store?.contacts?.phone2 || "");
                setLogoPreview(store?.logo || "");
                setBannerPreview(store?.banner || "");
            } catch (err) {
                toast.error(err?.response?.data?.message || "Failed to load settings");
            } finally {
                setIsLoading(false);
            }
        };

        getStore();
    }, [name]);

    // Upload helper
    const uploadToS3 = async (file) => {
        const presignRes = await api.post("/uploads/presign", {
            filename: file.name,
            contentType: file.type,
        });

        const { uploadUrl, key } = presignRes.data;

        const res = await fetch(uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": file.type },
            body: file,
        });

        if (!res.ok) throw new Error("Upload failed");

        return {
            key,
            type: "image",
            mimeType: file.type,
            name: file.name,
            size: file.size,
        };
    };

    // Logo
    const handleLogoUpload = async (e) => {
        e.preventDefault();
        if (!logo) return;

        try {
            setSavingLogo(true);
            const uploadedFile = await uploadToS3(logo);

            await api.post(`/stores/${name}/manage/logo`, {
                file: uploadedFile,
            });

            toast.success("Logo updated");
        } catch (err) {
            console.log(err);
            toast.error(err?.message || "Upload failed");
        } finally {
            setSavingLogo(false);
        }
    };

    // Banner
    const handleBannerUpload = async (e) => {
        e.preventDefault();
        if (!banner) return;

        try {
            setSavingBanner(true);
            const uploadedFile = await uploadToS3(banner);

            await api.post(`/stores/${name}/manage/banner`, {
                file: uploadedFile,
            });

            toast.success("Banner saved");
        } catch (err) {
            console.log(err);
            toast.error(err?.message || "Upload failed");
        } finally {
            setSavingBanner(false); // FIXED: Was setSavingLogo
        }
    };

    // Details
    const handleUpdateDetails = async (e) => {
        e.preventDefault();

        try {
            setSavingDetails(true);
            await api.put(`/${name}/manage/details`, {
                name: storeName,
                tagline,
                description,
                categories, // Sending the array
            });
            toast.success("Details updated");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Update failed");
        } finally {
            setSavingDetails(false);
        }
    };

    // Address
    const handleUpdateAddress = async (e) => {
        e.preventDefault();

        try {
            setSavingAddress(true);
            await api.put(`/${name}/manage/address`, {
                country,
                state,
                city,
                street,
                building,
                room,
                postalCode,
            });
            toast.success("Address updated");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Update failed");
        } finally {
            setSavingAddress(false);
        }
    };

    // Contacts
    const handleUpdateContacts = async (e) => {
        e.preventDefault();

        try {
            setSavingContacts(true);
            await api.put(`/${name}/manage/contacts`, {
                email,
                website,
                phone,
                phone2,
            });
            toast.success("Contacts updated");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Update failed");
        } finally {
            setSavingContacts(false);
        }
    };

    if (isLoading) {
        return (
            <div className="loader">
                <Loader />
            </div>
        );
    }

        // Delete account
        const handleDelete = async (e) => {
            e.preventDefault()
            window.confirm("delete this business?")
            try {
                setDeleting(true);
                await api.patch(`/stores/${name}/manage/delete`);
                toast.success("Account scheduled for deletion");
            } catch (err) {
                console.log(err)
                toast.error("Delete failed");
            } finally {
                setDeleting(false);
            }
        };

    return (
        <div className="settings-container">
            <header className="settings-header">
                <h1>Settings</h1>
                <p>Manage {name} public profile and contact details.</p>
            </header>

            {/* Branding */}
            <section className="settings-card">
                <h3>Branding</h3>
                <div className="logo-upload-section">
                    <div className="org-logo-preview">
                        {logoPreview ? (
                            <img src={logoPreview} alt="Logo" />
                        ) : (
                            <div className="placeholder">No Logo</div>
                        )}
                    </div>

                    <div className="upload-controls">
                        <input
                            type="file"
                            id="logo-input"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    setLogo(file);
                                    setLogoPreview(URL.createObjectURL(file));
                                }
                            }}
                        />

                        <button
                            className="btn-save"
                            onClick={handleLogoUpload}
                            disabled={savingLogo}
                        >
                            {savingLogo ? "Uploading..." : "Save Logo"}
                        </button>
                    </div>
                </div>
            </section>

            <section className="settings-card">
                <h3>Banner</h3>
                <div className="banner-upload-section">
                    <div className="banner-preview">
                        {bannerPreview ? (
                            <img src={bannerPreview} alt="Banner" />
                        ) : (
                            <div className="banner-placeholder">{name}</div>
                        )}
                    </div>

                    <div className="upload-controls">
                        <input
                            type="file"
                            id="banner-input"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    setBanner(file);
                                    setBannerPreview(URL.createObjectURL(file));
                                }
                            }}
                        />

                        <button
                            className="btn-save"
                            onClick={handleBannerUpload}
                            disabled={savingBanner}
                        >
                            {savingBanner ? "Uploading..." : "Save Banner"}
                        </button>
                    </div>
                </div>
            </section>

            {/* Details */}
            <section className="settings-card">
                <h3>General Details</h3>
                <form onSubmit={handleUpdateDetails}>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Store Name</label>
                            <input
                                value={storeName}
                                onChange={(e) => setStoreName(e.target.value)}
                            />
                        </div>

                        <div className="form-group">
                            <label>Tagline</label>
                            <input value={tagline} onChange={(e) => setTagline(e.target.value)} />
                        </div>

                        <div className="form-group full-width">
                            <label>Description</label>
                            <textarea rows="3" value={description} onChange={(e) => setDescription(e.target.value)} />
                        </div>

                        <div className="form-group full-width">
                            <h3>Categories (max 3)</h3>
                            <select onChange={e => addCategory(e.target.value)} defaultValue="">
                                <option value="" disabled>Select category</option>
                                {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>

                            <div className="chips">
                                {categories.map(c => (
                                    <span key={c} className="chip" onClick={() => removeCategory(c)}>{c} ×</span>
                                ))}
                            </div>
                        </div>
                    </div>

                    <button className="btn-save" type="submit" disabled={savingDetails}>
                        {savingDetails ? "Saving..." : "Update Details"}
                    </button>
                </form>
            </section>

            {/* Address */}
            <section className="settings-card">
                <h3>Address Information</h3>
                <form onSubmit={handleUpdateAddress}>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Country</label>
                            <input value={country} onChange={(e) => setCountry(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>State/Province</label>
                            <input value={state} onChange={(e) => setState(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>City</label>
                            <input value={city} onChange={(e) => setCity(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Postal Code</label>
                            <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Street</label>
                            <input value={street} onChange={(e) => setStreet(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Building & Room</label>
                            <div className="inline-inputs">
                                <input placeholder="Building" value={building} onChange={(e) => setBuilding(e.target.value)} />
                                <input placeholder="Room" value={room} onChange={(e) => setRoom(e.target.value)} />
                            </div>
                        </div>
                    </div>

                    <button className="btn-save" type="submit" disabled={savingAddress}>
                        {savingAddress ? "Saving..." : "Update Address"}
                    </button>
                </form>
            </section>

            {/* Contacts */}
            <section className="settings-card">
                <h3>Contact Information</h3>
                <form onSubmit={handleUpdateContacts}>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Public Email</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Website URL</label>
                            <input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Primary Phone</label>
                            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
                        </div>

                        <div className="form-group">
                            <label>Secondary Phone</label>
                            <input type="tel" value={phone2} onChange={(e) => setPhone2(e.target.value)} />
                        </div>
                    </div>

                    <button className="btn-save" type="submit" disabled={savingContacts}>
                        {savingContacts ? "Saving..." : "Update Contacts"}
                    </button>
                </form>
            </section>
            <section className="settings-card">
                <h3>Delete</h3>
                <form onSubmit={handleDelete}>
                    <div className="form-grid">
                        <div className="form-group">
                            <input 
                            type="text" 
                            value={typed} 
                            onChange={(e) => setTyped(e.target.value)}
                            placeholder="Type the word delete to continue" 
                            />
                        </div>
                    </div>
                    <button className="btn-save" type="submit" disabled={typed!=="delete"}>
                        {deleting ? "please wait..." : "Delete"}
                    </button>
                </form>
            </section>
        </div>
    );
};

export default OrgSettings;